import { Link, useNavigate } from 'react-router-dom';

interface Props {
  onClose: () => void;
  isLoggedIn?: boolean;
}

const STEPS = [
  {
    num: '01',
    icon: 'ri-user-check-line',
    color: 'text-teal-400',
    bg: 'bg-teal-500/15 border-teal-400/25',
    title: '建立帳號 / 登入',
    desc: '免費註冊，一分鐘完成，支援 LINE 快速登入',
  },
  {
    num: '02',
    icon: 'ri-upload-cloud-2-line',
    color: 'text-amber-400',
    bg: 'bg-amber-500/15 border-amber-400/25',
    title: '上傳物件・點擊出租',
    desc: '填寫房源資訊、上傳照片，設定租金即可發布',
  },
  {
    num: '03',
    icon: 'ri-robot-2-line',
    color: 'text-green-400',
    bg: 'bg-green-500/15 border-green-400/25',
    title: '加入官方 LINE 機器人',
    desc: '租客來訊時 AI 自動通知，隨時掌握看房動態',
  },
];

const AI_FEATURES = [
  {
    icon: 'ri-chat-ai-line',
    color: 'text-teal-300',
    text: 'AI 主動與租客聊天，收集居住人數、職業、聯絡電話',
  },
  {
    icon: 'ri-line-fill',
    color: 'text-green-300',
    text: '彙整完畢後即時推送 LINE 通知，讓您不錯過任何機會',
  },
  {
    icon: 'ri-megaphone-line',
    color: 'text-amber-300',
    text: '自動在 Threads、Facebook 等社群投放廣告擴大曝光',
  },
];

export default function GuestOnboardingModal({ onClose, isLoggedIn }: Props) {
  const navigate = useNavigate();

  const handleGoPublish = () => {
    onClose();
    navigate('/publish');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gray-900 border border-white/10 rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* 頂部光暈 */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-24 bg-teal-500/15 rounded-full blur-3xl pointer-events-none" />

        {/* 關閉 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>

        <div className="relative z-10 px-7 pt-8 pb-7">
          {/* 標題區 */}
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 border border-teal-400/30 rounded-full text-teal-300 text-xs font-bold mb-3">
              <i className="ri-sparkling-2-fill text-amber-400 animate-pulse" />
              屋主出租引導流程
            </div>
            <h2 className="text-xl font-black text-white leading-snug">
              3 步驟開始讓 AI 幫您招租
            </h2>
            <p className="text-white/45 text-xs mt-1.5">
              完全免費 · 零仲介費 · AI 全自動運作
            </p>
          </div>

          {/* 步驟預覽 */}
          <div className="space-y-2.5 mb-6">
            {STEPS.map((s, i) => (
              <div key={i} className={`flex items-center gap-3.5 border rounded-xl px-4 py-3 ${s.bg}`}>
                <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                  <span className="text-[10px] font-black text-white/30">{s.num}</span>
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-black/20">
                    <i className={`${s.icon} text-base ${s.color}`} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold leading-snug">{s.title}</p>
                  <p className="text-white/45 text-xs mt-0.5 leading-relaxed">{s.desc}</p>
                </div>
                {i === 0 && (
                  <div className="flex-shrink-0">
                    <span className="text-[10px] px-2 py-0.5 bg-teal-500/25 text-teal-300 rounded-full font-bold border border-teal-400/20">
                      先完成
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* AI 幫你做的事（摺疊預覽） */}
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-4 mb-6">
            <p className="text-white/40 text-[11px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <i className="ri-sparkling-2-fill text-amber-400" />
              完成後 AI 自動幫你
            </p>
            <div className="space-y-2">
              {AI_FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center rounded-md bg-white/8 flex-shrink-0 mt-0.5">
                    <i className={`${f.icon} text-xs ${f.color}`} />
                  </div>
                  <p className="text-white/55 text-xs leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 登入 / 注冊 / 直接發布 CTA */}
          <div className="space-y-2.5">
            {isLoggedIn ? (
              <button
                type="button"
                onClick={handleGoPublish}
                className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-black rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25"
              >
                <i className="ri-home-4-line text-base" />
                立即前往發布房源
                <i className="ri-arrow-right-line" />
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={onClose}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white font-black rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25"
                >
                  <i className="ri-login-box-line text-base" />
                  登入帳號，開始出租流程
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/register"
                  onClick={onClose}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-white/8 hover:bg-white/14 border border-white/15 text-white/80 hover:text-white font-bold rounded-xl transition-all text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line" />
                  免費建立帳號
                </Link>
              </>
            )}
          </div>

          <p className="text-white/25 text-[11px] text-center mt-3">
            {isLoggedIn ? '填寫房源資訊，讓 AI 幫你自動招租' : '登入後將自動開啟完整引導流程'}
          </p>
        </div>
      </div>
    </div>
  );
}
